/**
 * Shift entry. MASTER_PROMPT.md section 7.7.
 *
 * "If this is slower than Excel, staff will keep the spreadsheet and the
 * project fails." Everything here follows from that:
 *
 *  - full keyboard operation, no mouse needed
 *  - paste a block straight from Excel into the grid
 *  - live material balance as you type, so variance is visible before submit
 *  - one atomic submit for the whole shift, not a save per row
 *
 * And from the plant's own rules:
 *
 *  - a person sees only the machines they run, and a machine offers only its
 *    own materials in each dropdown — input, output and waste separately
 *  - waste fills itself in as input minus output, and stays editable; what
 *    the waste figure does not cover is the unaccounted quantity, which needs
 *    a reason once it is outside the machine's tolerance
 *  - the same screen edits an entry already made, with a reason for the edit
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, OfflineQueued } from "../../lib/api";
import {
  controls,
  materialsFor,
  type ContextItem,
  type Direction,
  type EntryContext,
} from "../../lib/controls";
import type { User } from "../../lib/types";
import { fmtPct, fmtQty, fmtShift, today } from "../../lib/format";

type Section = "consume" | "produce" | "loss";

interface Row {
  key: string;
  itemCode: string;
  qty: string;
  error?: string;
}

const SECTIONS: {
  id: Section;
  direction: Direction;
  title: string;
  hint: string;
}[] = [
  { id: "consume", direction: "INPUT", title: "Input", hint: "What went into the machine" },
  { id: "produce", direction: "OUTPUT", title: "Output", hint: "What came out" },
  {
    id: "loss",
    direction: "WASTE",
    title: "Waste",
    hint: "Filled in as input minus output — change it if you weighed it",
  },
];

const blank = (itemCode = ""): Row => ({
  key: Math.random().toString(36).slice(2),
  itemCode,
  qty: "",
});

const num = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

interface Props {
  user: User;
  /** An entry to edit instead of a new shift. */
  editId?: number | null;
  /** Called after an edit is saved or abandoned. */
  onDone?: () => void;
}

export function ShiftEntry({ user, editId = null, onDone }: Props) {
  const [ctx, setCtx] = useState<EntryContext | null>(null);
  const [machineCode, setMachineCode] = useState("");
  const [businessDate, setBusinessDate] = useState(() => today());
  const [shift, setShift] = useState<"DAY" | "NIGHT">("DAY");
  const [hours, setHours] = useState("");
  const [manpower, setManpower] = useState("");
  const [power, setPower] = useState("");

  const [rows, setRows] = useState<Record<Section, Row[]>>({
    consume: [blank(), blank()],
    produce: [blank()],
    loss: [blank()],
  });
  // Waste follows input minus output until somebody types a waste figure of
  // their own. From then on it is theirs, until they ask for it back.
  const [wasteTouched, setWasteTouched] = useState(false);

  const [reasonCodeId, setReasonCodeId] = useState<number | "">("");
  const [note, setNote] = useState("");
  const [editReason, setEditReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(Boolean(editId));
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);

  // Loom floor has its own screen, with looms, operators and rolls.
  const machines = useMemo(
    () => (ctx?.machines ?? []).filter((m) => m.code !== "LOOM_FLOOR"),
    [ctx]
  );
  const machine = machines.find((m) => m.code === machineCode);
  const tolerance = machine ? Number(machine.tolerance_pct) * 100 : 0;

  const options = useMemo(
    () => ({
      consume: materialsFor(ctx, machine, "INPUT"),
      produce: materialsFor(ctx, machine, "OUTPUT"),
      loss: materialsFor(ctx, machine, "WASTE"),
    }),
    [ctx, machine]
  );

  useEffect(() => {
    controls
      .context()
      .then((c) => {
        setCtx(c);
        const usable = c.machines.filter((m) => m.code !== "LOOM_FLOOR");
        if (!editId && usable.length) setMachineCode(usable[0].code);
      })
      .catch((e: ApiError) =>
        setBanner({ tone: "negative", text: `Could not load your machines: ${e.message}` })
      );
  }, [editId]);

  // Editing: fill the screen from the entry as it stands.
  useEffect(() => {
    if (!editId) return;
    setLoadingEdit(true);
    controls
      .entry(editId)
      .then((entry) => {
        setMachineCode(entry.machine_code);
        setBusinessDate(entry.business_date);
        setShift(entry.shift);
        setHours(entry.running_hours ?? "");
        setManpower(entry.manpower_count?.toString() ?? "");
        setPower(entry.power_units ?? "");
        const pick = (type: string, sourced = true) =>
          entry.lines
            .filter((l) => l.type === type && (!sourced || l.from !== null))
            .map((l) => ({ ...blank(l.item_code), qty: l.qty }));
        const withSpare = (list: Row[]) => [...list, blank()];
        setRows({
          consume: withSpare(pick("ISSUE")),
          produce: withSpare(pick("PRODUCTION_OUT")),
          loss: withSpare(pick("LOSS")),
        });
        setWasteTouched(true);
        setReasonCodeId(entry.reason_code_id ?? "");
        setNote(entry.variance_note ?? "");
        if (!entry.can_change) {
          setBanner({
            tone: "warning",
            text: entry.cannot_change_because ?? "This entry cannot be changed.",
          });
        }
      })
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }))
      .finally(() => setLoadingEdit(false));
  }, [editId]);

  // A waste row needs a material before a figure means anything. Where the
  // machine has exactly one waste material, it is chosen for you.
  useEffect(() => {
    if (editId || !machine) return;
    const wasteList = machine.materials.WASTE;
    const only = wasteList && wasteList.length === 1 ? wasteList[0] : "";
    const firstWasteType =
      options.loss.find((i) => i.item_type === "WASTE")?.code ?? "";
    setRows({
      consume: [blank(), blank()],
      produce: [blank()],
      loss: [blank(only || firstWasteType)],
    });
    setWasteTouched(false);
    // Only when the machine changes: a new machine is a new list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineCode]);

  // -- Live material balance ---------------------------------------------
  const totals = useMemo(() => {
    const total = (section: Section) =>
      rows[section].reduce((sum, r) => sum + num(r.qty), 0);
    return { input: total("consume"), output: total("produce") };
  }, [rows]);

  const autoWaste = Math.max(totals.input - totals.output, 0);

  // Until somebody types a waste figure, the first waste row *is* input minus
  // output — computed here, in the same render, rather than written back by
  // an effect. An effect lands a render late, and a quick submit straight
  // after typing the output then went out with the previous waste figure.
  const lossRows = useMemo(() => {
    if (wasteTouched) return rows.loss;
    const [first = blank(), ...rest] = rows.loss;
    return [
      { ...first, qty: autoWaste ? String(Number(autoWaste.toFixed(3))) : "" },
      ...rest,
    ];
  }, [rows.loss, wasteTouched, autoWaste]);
  const shown: Record<Section, Row[]> = { ...rows, loss: lossRows };

  const balance = useMemo(() => {
    const waste = lossRows.reduce((sum, r) => sum + num(r.qty), 0);
    const variance = totals.input - totals.output - waste;
    const pct = totals.input ? (variance / totals.input) * 100 : 0;
    return {
      ...totals,
      waste,
      variance,
      pct,
      within: totals.input === 0 || Math.abs(pct) <= tolerance + 1e-9,
    };
  }, [lossRows, totals, tolerance]);

  const needsReason = !balance.within && balance.input > 0;
  const reasons = ctx?.reason_codes ?? [];
  const selectedReason = reasons.find((r) => r.id === reasonCodeId);

  // -- Grid editing -------------------------------------------------------

  function update(section: Section, index: number, patch: Partial<Row>) {
    const takeOver = section === "loss" && patch.qty !== undefined && !wasteTouched;
    if (section === "loss" && patch.qty !== undefined) setWasteTouched(true);
    setRows((prev) => {
      // The first time waste is typed into, start from what was on screen.
      const base = takeOver ? { ...prev, loss: lossRows } : prev;
      const next = { ...base, [section]: [...base[section]] };
      next[section][index] = { ...next[section][index], ...patch, error: undefined };
      // Keep one spare row so there is always somewhere to tab into.
      if (index === next[section].length - 1 && (patch.itemCode || patch.qty)) {
        next[section] = [...next[section], blank()];
      }
      return next;
    });
  }

  function removeRow(section: Section, index: number) {
    setRows((prev) => {
      const list = prev[section].filter((_, i) => i !== index);
      return { ...prev, [section]: list.length ? list : [blank()] };
    });
  }

  /**
   * Paste a block from Excel. Two tab-separated columns, material code and
   * quantity, one row per line. A code that is not on the machine's list is
   * kept and marked, so the person sees exactly which line to fix.
   */
  function handlePaste(section: Section, index: number, event: React.ClipboardEvent) {
    const text = event.clipboardData.getData("text/plain");
    if (!text.includes("\t") && !text.includes("\n")) return;
    event.preventDefault();

    const allowed = new Set(options[section].map((i) => i.code));
    const parsed = text
      .split(/\r?\n/)
      .map((line) => line.split("\t"))
      .filter((cells) => cells.some((c) => c.trim()))
      .map((cells) => {
        const code = (cells[0] ?? "").trim().toUpperCase();
        return {
          key: Math.random().toString(36).slice(2),
          itemCode: code,
          qty: (cells[1] ?? "").replace(/[, ]/g, "").trim(),
          error: code && !allowed.has(code)
            ? `${code} is not a ${section === "consume" ? "input" : section === "produce" ? "output" : "waste"} material of this machine`
            : undefined,
        };
      });
    if (section === "loss") setWasteTouched(true);

    setRows((prev) => {
      const list = [...prev[section]];
      list.splice(index, parsed.length, ...parsed);
      return { ...prev, [section]: [...list, blank()] };
    });
  }

  function onKeyDown(event: React.KeyboardEvent) {
    // Enter moves down a row, matching spreadsheet muscle memory.
    if (event.key === "Enter" && !event.shiftKey &&
        (event.target as HTMLElement).tagName !== "TEXTAREA") {
      event.preventDefault();
      const inputs = Array.from(
        gridRef.current?.querySelectorAll<HTMLElement>("input,select") ?? []
      );
      const here = inputs.indexOf(event.target as HTMLElement);
      inputs[Math.min(here + 2, inputs.length - 1)]?.focus();
    }
  }

  // -- Submit -------------------------------------------------------------

  function collect(section: Section) {
    return shown[section]
      .filter((r) => r.itemCode.trim() && num(r.qty) > 0)
      .map((r) => ({ item_code: r.itemCode.trim().toUpperCase(), qty: num(r.qty) }));
  }

  function reset() {
    const wasteList = machine?.materials.WASTE;
    const only = wasteList && wasteList.length === 1 ? wasteList[0] : "";
    setRows({ consume: [blank(), blank()], produce: [blank()], loss: [blank(only)] });
    setWasteTouched(false);
    setReasonCodeId("");
    setNote("");
    setHours("");
    setManpower("");
    setPower("");
  }

  async function submit() {
    setBanner(null);
    if (!machineCode) {
      setBanner({ tone: "negative", text: "Choose a machine." });
      return;
    }
    const lossWithoutMaterial = lossRows.some((r) => num(r.qty) > 0 && !r.itemCode);
    if (lossWithoutMaterial) {
      setBanner({ tone: "warning", text: "Choose the waste material for the waste quantity." });
      return;
    }
    if (rows.consume.concat(rows.produce, rows.loss).some((r) => r.error)) {
      setBanner({ tone: "warning", text: "Fix the marked rows first." });
      return;
    }
    if (needsReason && !reasonCodeId) {
      setBanner({
        tone: "warning",
        text: `${fmtQty(Math.abs(balance.variance), 2)} kg is unaccounted — ${fmtPct(Math.abs(balance.pct))}, outside the ${fmtPct(tolerance)} tolerance for ${machine?.name}. Choose a reason to submit.`,
      });
      return;
    }
    if (selectedReason?.requires_note && !note.trim()) {
      setBanner({
        tone: "warning",
        text: `"${selectedReason.description}" needs a note explaining what happened.`,
      });
      return;
    }
    if (editId && editReason.trim().length < 3) {
      setBanner({ tone: "warning", text: "Say why this entry is being changed." });
      return;
    }

    const body = {
      machine_code: machineCode,
      business_date: businessDate,
      shift,
      running_hours: hours ? num(hours) : null,
      manpower_count: manpower ? Math.round(num(manpower)) : null,
      power_units: power ? num(power) : null,
      consume: collect("consume"),
      produce: collect("produce"),
      loss: collect("loss"),
    };

    setBusy(true);
    try {
      if (editId) {
        await controls.editShift(editId, {
          ...body,
          edit_reason: editReason.trim(),
          reason_code_id: reasonCodeId === "" ? null : reasonCodeId,
          variance_note: note || null,
        });
        setBanner({ tone: "positive", text: "Entry changed. The old figures are kept in its history." });
        onDone?.();
        return;
      }
      // One atomic submit for the whole shift (section 7.7).
      const run = await api.createRun(body);
      await api.submitRun(
        run.id,
        reasonCodeId === "" ? undefined : reasonCodeId,
        note || undefined
      );
      setBanner({
        tone: "positive",
        text: `${machine?.name} · ${fmtShift(shift)} shift submitted.`,
      });
      reset();
    } catch (err) {
      if (err instanceof OfflineQueued) {
        setBanner({
          tone: "warning",
          text: "No connection. Saved on this device and it will sync automatically.",
        });
        return;
      }
      const e = err as ApiError;
      if (e.blocked) {
        // Negative stock is set to block in Settings: mark the row it hit.
        const name = e.blocked.item;
        setRows((prev) => ({
          ...prev,
          consume: prev.consume.map((r) =>
            ctx?.items.find((i) => i.code === r.itemCode)?.name === name
              ? {
                  ...r,
                  error: `Only ${fmtQty(e.blocked!.available, 3)} available — short by ${fmtQty(e.blocked!.short_by, 3)}`,
                }
              : r
          ),
        }));
        setBanner({ tone: "negative", text: e.blocked.message });
        return;
      }
      setBanner({ tone: "negative", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  if (ctx && machines.length === 0) {
    return (
      <div className="page">
        <div className="state state--empty">
          <span className="state__glyph" aria-hidden="true">▤</span>
          <h3>No machines assigned</h3>
          <p>
            {user.name} is not assigned to any machine yet
            {ctx.machines.some((m) => m.code === "LOOM_FLOOR")
              ? " apart from the loom floor, which has its own screen"
              : ""}
            . An administrator adds machines to your login in Settings.
          </p>
        </div>
      </div>
    );
  }

  const unaccountedTone =
    balance.input === 0 ? "idle" : balance.within ? "ok" : "off";

  return (
    <div className="entry">
      <div className="entry__header">
        <div>
          <h2 className="entry__title">{editId ? "Edit shift entry" : "Shift entry"}</h2>
          <p className="entry__sub">
            {editId
              ? "Saving reverses the old figures and posts these. Both stay in the history."
              : `${user.name} · one submit for the whole shift`}
          </p>
        </div>

        <div className="entry__meta">
          <label className="inputgroup">
            <span className="label">Machine</span>
            <select
              value={machineCode}
              onChange={(e) => setMachineCode(e.target.value)}
              disabled={Boolean(editId)}
            >
              {machines.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="inputgroup">
            <span className="label">Date</span>
            <input
              type="date"
              value={businessDate}
              onChange={(e) => setBusinessDate(e.target.value)}
            />
          </label>
          <label className="inputgroup">
            <span className="label">Shift</span>
            <select
              value={shift}
              onChange={(e) => setShift(e.target.value as "DAY" | "NIGHT")}
            >
              <option value="DAY">Day</option>
              <option value="NIGHT">Night</option>
            </select>
          </label>
          <label className="inputgroup inputgroup--sm">
            <span className="label">Hours</span>
            <input className="num" inputMode="decimal" value={hours}
                   onChange={(e) => setHours(e.target.value)} />
          </label>
          <label className="inputgroup inputgroup--sm">
            <span className="label">Manpower</span>
            <input className="num" inputMode="numeric" value={manpower}
                   onChange={(e) => setManpower(e.target.value)} />
          </label>
          <label className="inputgroup inputgroup--sm">
            <span className="label">Power kWh</span>
            <input className="num" inputMode="decimal" value={power}
                   onChange={(e) => setPower(e.target.value)} />
          </label>
        </div>
      </div>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {machine && Object.values(machine.materials).some((l) => l === null) && (
        <p className="section-note">
          {machine.name} has no material list for{" "}
          {SECTIONS.filter((s) => machine.materials[s.direction] === null)
            .map((s) => s.title.toLowerCase())
            .join(", ")}{" "}
          yet, so every material is offered there. An administrator sets the
          list in Settings.
        </p>
      )}

      {loadingEdit ? (
        <div className="state">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton state__bar" />)}
        </div>
      ) : (
        <div className="entry__body" ref={gridRef} onKeyDown={onKeyDown}>
          <div className="entry__sections">
            {SECTIONS.map((section) => (
              <section key={section.id} className="grid">
                <header className="grid__head">
                  <h3 className="grid__title">{section.title}</h3>
                  <span className="grid__hint">{section.hint}</span>
                </header>

                <table className="grid__table">
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th className="num">Kg</th>
                      <th aria-label="Remove" />
                    </tr>
                  </thead>
                  <tbody>
                    {shown[section.id].map((row, index) => (
                      <tr key={row.key}
                          className={row.error ? "grid__row--error" : undefined}>
                        <td>
                          <MaterialSelect
                            value={row.itemCode}
                            options={options[section.id]}
                            onChange={(code) => update(section.id, index, { itemCode: code })}
                          />
                          {row.error && <span className="grid__error">{row.error}</span>}
                        </td>
                        <td>
                          <input
                            className="num mono"
                            inputMode="decimal"
                            value={row.qty}
                            placeholder="0"
                            onChange={(e) => update(section.id, index, { qty: e.target.value })}
                            onPaste={(e) => handlePaste(section.id, index, e)}
                          />
                          {section.id === "loss" && index === 0 && (
                            <span className="grid__auto">
                              {wasteTouched ? (
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--auto btn--sm"
                                  onClick={() => setWasteTouched(false)}
                                  title="Set waste back to input minus output"
                                >
                                  Use {fmtQty(autoWaste, 2)}
                                </button>
                              ) : (
                                "auto: input − output"
                              )}
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            className="grid__remove"
                            onClick={() => removeRow(section.id, index)}
                            aria-label="Remove row"
                            tabIndex={-1}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}
          </div>

          {/* Live balance — the supervisor sees the hole before they submit,
              not three weeks later at month-end. */}
          <aside className="balance">
            <h3 className="balance__title">Material balance</h3>

            <dl className="balance__list">
              <div>
                <dt>Input</dt>
                <dd className="num mono">{fmtQty(balance.input, 2)}</dd>
              </div>
              <div>
                <dt>Output</dt>
                <dd className="num mono">{fmtQty(balance.output, 2)}</dd>
              </div>
              <div>
                <dt>Waste</dt>
                <dd className="num mono">{fmtQty(balance.waste, 2)}</dd>
              </div>
            </dl>

            <div className={`balance__result balance__result--${unaccountedTone}`}>
              <span className="label">Unaccounted</span>
              <span className="balance__value num mono">
                {fmtQty(balance.variance, 2)}
              </span>
              <span className="balance__pct">
                {balance.input === 0
                  ? "Nothing entered yet"
                  : `${fmtPct(balance.pct)} · tolerance ${fmtPct(tolerance)}`}
              </span>
            </div>

            <div className="balance__reason">
              <label className="inputgroup">
                <span className="label">
                  Reason {needsReason ? "— required" : "(optional)"}
                </span>
                <select
                  value={reasonCodeId}
                  onChange={(e) =>
                    setReasonCodeId(e.target.value === "" ? "" : Number(e.target.value))
                  }
                >
                  <option value="">Choose…</option>
                  {reasons.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.description}
                    </option>
                  ))}
                </select>
              </label>
              {(selectedReason?.requires_note || note) && (
                <label className="inputgroup">
                  <span className="label">
                    Note {selectedReason?.requires_note ? "— required" : ""}
                  </span>
                  <textarea rows={3} value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="What happened?" />
                </label>
              )}
              {needsReason && (
                <p className="balance__escalation">
                  This will go to the Unit Head for approval.
                </p>
              )}
            </div>

            {editId && (
              <label className="inputgroup">
                <span className="label">Why is this being changed? — required</span>
                <textarea rows={2} value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          placeholder="e.g. output reweighed" />
              </label>
            )}

            <button className="btn" onClick={submit} disabled={busy}>
              {busy ? <span className="spinner" /> : null}
              {busy ? "Saving" : editId ? "Save changes" : "Submit shift"}
            </button>
            {editId && (
              <button className="btn btn--ghost" onClick={() => onDone?.()} disabled={busy}>
                Cancel
              </button>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

/** A material dropdown that still shows a code it does not offer, so an
 *  edited or pasted row never silently loses its material. */
export function MaterialSelect({
  value,
  options,
  onChange,
  placeholder = "Choose…",
}: {
  value: string;
  options: ContextItem[];
  onChange: (code: string) => void;
  placeholder?: string;
}) {
  const known = options.some((o) => o.code === value);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {value && !known && (
        <option value={value}>{value} (not on this machine's list)</option>
      )}
      {options.map((o) => (
        <option key={o.code} value={o.code}>
          {o.name} · {o.code}
        </option>
      ))}
    </select>
  );
}
